import {
  unwrapFieldValue,
  withFieldOrigin,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { normalizeZoneBoundsStorage } from 'features/libra/stepComponents/zoneAnalysis/zoneBoundsStorage'
import { inheritCopiedOrigin } from 'features/libra/ticket/fieldOrigin'
import {
  LibraTicket,
  parseLibraTicketEntry,
} from 'features/libra/ticket/libraTicketsApi'
import { getLatestVersions } from 'features/libra/ticket/ticketVersioning'
import { GeckoEntry, RolodexEntryTagsType, RolodexEntryType } from 'types'
import { z } from 'zod'

import { PlanningSourceCapture, sourceZonesSchema } from './planningTypes'
import { buildSourceContext, sourceContextStepSchema } from './sourceContext'

const metadataSchema = z.record(z.string(), z.json())

export const planningSourceWorkflowSchema = z.object({
  id: z.string(),
  data: z.object({
    steps: z.array(sourceContextStepSchema),
    globalContext: z.object({
      inspectionPlanningSource: z.object({
        zoneStepId: z.string(),
        contextStepIds: z.array(z.string()),
      }),
      ontologyFields: z.record(z.string(), metadataSchema),
    }),
  }),
})

type PlanningSourceWorkflow = z.infer<typeof planningSourceWorkflowSchema>

export function getPlanningSources(
  entries: readonly GeckoEntry[],
  workflows: readonly PlanningSourceWorkflow[],
  targetTags: RolodexEntryTagsType
) {
  const scopeTags = ['organization', 'unit', 'libra-ticket-group'] as const
  if (scopeTags.some((tag) => !targetTags[tag])) return []
  const tickets = entries.flatMap((entry) => {
    if (
      entry.type !== RolodexEntryType.LIBRA_TICKET ||
      !scopeTags.every((tag) => entry.tags[tag] === targetTags[tag]) ||
      !workflows.some(
        (workflow) => workflow.id === entry.tags['libra-workflow']
      )
    )
      return []
    const ticket = parseLibraTicketEntry(entry)
    return ticket?.workflowComplete ? [ticket] : []
  })
  return getLatestVersions(tickets)
}

export function capturePlanningSource(
  source: LibraTicket,
  workflow: PlanningSourceWorkflow,
  config: PlanningSourceCapture,
  capturedAt: string
): FieldValues {
  if (!source.workflowComplete || source.workflowId !== workflow.id)
    throw new Error('Select a completed NII assessment')

  const { inspectionPlanningSource, ontologyFields } =
    workflow.data.globalContext
  const { steps } = workflow.data
  const zoneConfig = steps.find(
    (step) => step.id === inspectionPlanningSource.zoneStepId
  )?.config
  const zoneField = zoneConfig?.zoneFieldName
  if (!zoneField)
    throw new Error('The source workflow must configure its zone field')
  const fields = source.fieldValues
  const sourceZoneValue = unwrapFieldValue(fields[zoneField])
  const normalizedSourceZoneValue = Array.isArray(sourceZoneValue)
    ? normalizeZoneBoundsStorage(sourceZoneValue)
    : sourceZoneValue
  const zones = sourceZonesSchema.parse(normalizedSourceZoneValue)
  if (!zones.length)
    throw new Error('The completed NII assessment has no confirmed zones')

  const capturedSteps = inspectionPlanningSource.contextStepIds.map((id) => {
    const step = steps.find((item) => item.id === id)
    if (!step) throw new Error(`Source context step not found: ${id}`)
    return step
  })
  const context = buildSourceContext(
    capturedSteps,
    ontologyFields,
    fields,
    zoneField
  )
  const copiedZoneField = inheritCopiedOrigin(fields[zoneField])

  return structuredClone({
    ...Object.fromEntries(
      Object.entries(config.copyFields).flatMap(([from, to]) =>
        fields[from] === undefined
          ? []
          : [[to, inheritCopiedOrigin(fields[from])]]
      )
    ),
    [config.sourceZonesField]: {
      ...copiedZoneField,
      value: normalizedSourceZoneValue,
    },
    [config.sourceContextField]: withFieldOrigin(
      wrapFieldValue(context),
      'system'
    ),
    [config.sourceField]: withFieldOrigin(
      wrapFieldValue({
        ticketId: source.id,
        title: source.title,
        version: source.version,
        workflowId: source.workflowId,
        capturedAt,
      }),
      'system'
    ),
  })
}
